import type {
	BaseItemContent,
	Choice,
	FlashcardEntry,
	Media,
	SessionItem,
	SessionItemAnswer,
	SituationCard,
} from "@memorang/types/src/session/new-session";

export type RawProethicaSessionItem = {
	id?: string;
	type: string;
	position: number;
	title?: string;
	instruction?: string;
	content?: unknown;
	feedback?: string;
	scenarioId?: string;
	answer?: SessionItemAnswer | null;
	lastUpdated?: boolean;
};

type RawItem = RawProethicaSessionItem;

type RawMedia = {
	type: string;
	url: string;
	title?: string;
	transcript?: string;
	instruction?: string;
	buttonText?: string;
};

type RawOption = {
	id: string;
	text: string;
	isCorrect: boolean;
	description?: string;
	feedback?: string;
};

type RawCard = {
	front: {
		title: string;
		imageUrl?: string;
		illustration?: string;
		illustrationImage?: string;
		illustrationUrl?: string;
		situation?: string;
	};
	back: {
		content?: string;
		outcomes?: { id: string; label: string; content: string }[];
	};
};

// Fallback only. Session items carry the real backend item id (item.id), which
// MUST be used so live answer-events reconcile against the session on the server
// (a synthetic id posts an event the backend can't match -> item scored as skipped).
const baseId = 70001;

const toMedia = (mediaArr: RawMedia[], position: number): Media[] =>
	mediaArr.map((item, index) => ({
		id: `proethica-${position}-media-${index + 1}`,
		type: (item.type?.toLowerCase() as Media["type"]) ?? "video",
		url: item.url,
		title: item.title,
		transcript: item.transcript,
		instruction: item.instruction,
		buttonText: item.buttonText,
	}));

const toChoices = (options: RawOption[]): Choice[] =>
	options.map((opt) => ({
		id: opt.id,
		text: opt.text,
		stem: opt.text,
		isCorrect: opt.isCorrect,
		explanation: opt.feedback ?? undefined,
		description: opt.description,
	}));

const toFlashcards = (cards: RawCard[]): FlashcardEntry[] =>
	cards.map((card) => ({
		front: {
			text: card.front.title,
			...(card.front.imageUrl ? { image: card.front.imageUrl } : {}),
			...(card.front.illustration
				? { illustration: card.front.illustration }
				: {}),
			...(card.front.illustrationImage
				? { illustrationImage: card.front.illustrationImage }
				: {}),
			...(card.front.illustrationUrl
				? { illustrationUrl: card.front.illustrationUrl }
				: {}),
		},
		back:
			card.back.content ??
			(card.back.outcomes ?? [])
				.map((o) => `**${o.label}**\n\n${o.content}`)
				.join("\n\n---\n\n"),
	}));

const withTitle = (title: string | undefined, body: string) =>
	[title ? `## ${title}` : "", body].filter(Boolean).join("\n\n");

type BuildSessionItemsOptions = {
	moduleLabel: string;
};

const buildModuleLanding = (
	item: RawItem,
	id: number,
	opts: BuildSessionItemsOptions,
): SessionItem => {
	const c = item.content as {
		module?: {
			label?: string;
			title?: string;
			badgeSource?: string | number | { uri: string };
			durationLabel?: string;
			activityCount?: number;
			knowledgeCheckQuestionCount?: number;
		};
		lesson: { heading: string; text: string };
		outline: {
			heading: string;
			sections: {
				title: string;
				items: (string | { title: string; icon?: string })[];
			}[];
		};
		learningObjectives: { heading: string; intro: string; items: string[] };
	};

	return {
		type: "ITEM",
		id,
		content: {
			stem: "",
			instruction: "",
			moduleLanding: {
				module: c.module,
				lesson: c.lesson,
				outline: c.outline,
				learningObjectives: c.learningObjectives,
			},
		},
		metadata: {
			type: "SCENARIO_INTRO",
			taskType: "MODULE_LANDING",
			skill: "READING",
			title: opts.moduleLabel,
			topics: ["Overview"],
		},
	};
};

const buildScenario = (item: RawItem, id: number): SessionItem => {
	const c = item.content as {
		text?: string;
		textAfter?: string;
		media?: RawMedia[];
		steps?: { title: string; description: string }[];
		scenarioPractice?: {
			info?: string;
			scenario: string;
			instruction?: string;
			media?: { url: string; title?: string }[];
		};
	};

	const hasSteps = Boolean(c.steps?.length);
	const practice = c.scenarioPractice;

	return {
		type: "ITEM",
		id,
		content: {
			// Practice owns its own info/instruction blocks, so keep the stem to the
			// title only and don't double-render the instruction below it.
			stem: withTitle(item.title, practice ? "" : (c.text ?? "")),
			instruction: practice ? "" : (item.instruction ?? ""),
			media: toMedia(c.media ?? [], item.position),
			...(practice ? { scenarioPractice: practice } : {}),
			...(c.textAfter ? { textAfter: c.textAfter } : {}),
			...(hasSteps ? { steps: c.steps } : {}),
		},
		metadata: {
			type: "SCENARIO_INTRO",
			...(hasSteps ? { taskType: "SCENARIO_STEPS" } : {}),
			skill: "READING",
			title: item.title ?? "",
			topics: ["Scenario"],
		},
	};
};

const buildLesson = (item: RawItem, id: number): SessionItem => {
	const c = item.content as { text?: string; lesson?: string };

	return {
		type: "ITEM",
		id,
		content: {
			stem: withTitle(item.title, c.text ?? ""),
			instruction: item.instruction ?? "",
			lessonText: c.lesson ?? "",
		},
		metadata: {
			type: "SCENARIO_INTRO",
			taskType: "LESSON",
			skill: "READING",
			title: item.title ?? "",
			topics: ["Lesson"],
		},
	};
};

type RawPerspective = {
	perspective: string;
	// Full principle block; the first line is the principle name and is used to
	// resolve the reused principle illustration at render time.
	principle: string;
};

const buildPerspective = (item: RawItem, id: number): SessionItem => {
	const c = item.content as {
		text?: string;
		perspectives?: RawPerspective[];
	};
	const perspectives = c.perspectives ?? [];

	const cards: FlashcardEntry[] = perspectives.map((p) => ({
		front: { text: p.perspective },
		back: p.principle,
	}));

	return {
		type: "ITEM",
		id,
		content: {
			stem: withTitle(item.title, c.text ?? ""),
			instruction: item.instruction ?? "",
			cards,
		},
		metadata: {
			type: "FLASHCARD_DECK",
			taskType: "PERSPECTIVE_GRID",
			skill: "READING",
			title: item.title ?? "",
			topics: ["Perspectives"],
		},
	};
};

const buildReflect = (item: RawItem, id: number): SessionItem => {
	const c = item.content as {
		stem?: string;
		options?: RawOption[];
	};
	const options = c.options ?? [];
	const choices = toChoices(options);

	return {
		type: "ITEM",
		id,
		content: {
			stem: withTitle(item.title, c.stem ?? ""),
			instruction: item.instruction ?? "",
			choices,
		},
		metadata: {
			type: "BINARY_CHOICE",
			skill: "READING",
			title: item.title ?? "",
			topics: ["Reflect"],
		},
		response: {
			maximumScore: 1,
			correctResponse: options
				.filter((o) => o.isCorrect)
				.map((o) => ({ id: o.id })),
			numChoicesRequired: { min: 1, max: 1 },
		},
	};
};

const buildExplore = (
	item: RawItem,
	id: number,
	reviewItem: RawItem | undefined,
): SessionItem => {
	const c = item.content as {
		stem?: string;
		principles?: { title: string; description: string }[];
		principlesLayout?: "tabs" | "accordion";
		question?: { type: string; stem?: string; options?: RawOption[] };
	};
	const options = c.question?.options ?? [];

	const reviewCards: FlashcardEntry[] = reviewItem
		? toFlashcards(
				((reviewItem.content as { cards?: RawCard[] }).cards ??
					[]) as RawCard[],
			)
		: [];

	const principles = c.principles ?? [];

	return {
		type: "ITEM",
		id,
		content: {
			stem: withTitle(item.title, c.stem ?? ""),
			instruction: item.instruction ?? "",
			principles,
			principlesLayout: c.principlesLayout,
			questionStem: c.question?.stem,
			choices: options.map((opt) => ({
				id: opt.id,
				text: opt.text,
				stem: opt.text,
				isCorrect: opt.isCorrect,
			})),
			reviewCards,
		},
		stimulus:
			principles.length && options.length
				? {
						id: id + 40000,
						content: {
							instruction: "",
							passage: {
								title: "PROETHICS Principles",
								paragraphs: principles.map((p, i) => ({
									number: i + 1,
									text: `**${p.title}**\n\n${p.description}`,
								})),
							},
						},
						metadata: {
							type: "READING" as const,
							title: "PROETHICS Principles",
							skill: "READING" as const,
							topics: ["Principles"],
						},
					}
				: undefined,
		metadata: {
			type: "EXPLORE_ACCORDION",
			skill: "READING",
			title: item.title ?? "",
			topics: ["Explore"],
		},
		response: {
			maximumScore: options.length ? 1 : 0,
			correctResponse: options
				.filter((o) => o.isCorrect)
				.map((o) => ({ id: o.id })),
			numChoicesRequired: {
				min: options.length ? 1 : 0,
				max: options.length,
			},
		},
	};
};

const buildReviewFlashcard = (item: RawItem, id: number): SessionItem => {
	const c = item.content as { stem?: string; cards?: RawCard[] };

	return {
		type: "ITEM",
		id,
		content: {
			stem: withTitle(item.title, c.stem ?? ""),
			instruction: item.instruction ?? "",
			cards: toFlashcards(c.cards ?? []),
		},
		metadata: {
			type: "FLASHCARD_DECK",
			taskType: "REVIEW_GRID",
			skill: "READING",
			title: item.title ?? "",
			topics: ["Review"],
		},
	};
};

const buildAnalysisFlashcard = (item: RawItem, id: number): SessionItem => {
	const c = item.content as { stem?: string; cards?: RawCard[] };

	return {
		type: "ITEM",
		id,
		content: {
			stem: withTitle(item.title, c.stem ?? ""),
			instruction: item.instruction ?? "",
			cards: toFlashcards(c.cards ?? []),
		},
		metadata: {
			type: "FLASHCARD_DECK",
			taskType: "ANALYSIS_TABS",
			skill: "READING",
			title: item.title ?? "",
			topics: ["Analysis"],
		},
	};
};

type RawSituationCard = {
	front: { title?: string; situation?: string };
	back: {
		outcomes?: {
			id: string;
			label: string;
			front?: string;
			content: string;
		}[];
	};
};

const buildEthicalSituation = (item: RawItem, id: number): SessionItem[] => {
	const c = item.content as {
		stem?: string;
		text?: string;
		cards?: RawSituationCard[];
	};
	const cards = c.cards ?? [];
	const total = cards.length;

	return cards.map((card, situationIndex) => {
		const situation: SituationCard = {
			front: {
				title: card.front.title ?? "",
				text: card.front.situation ?? "",
			},
			back: {
				outcomes: (card.back.outcomes ?? []).map((o) => ({
					label: o.label,
					front: o.front ?? "",
					content: o.content,
				})),
			},
		};

		return {
			type: "ITEM",
			id: id * 1000 + situationIndex,
			content: {
				// Info lead-in sits in the stem body so it renders above the
				// Directions instruction (StemContainer draws stem before it).
				stem: withTitle(item.title, c.text ?? ""),
				instruction: item.instruction ?? "",
				situations: [situation],
				situationIndex,
				situationTotal: total,
			},
			metadata: {
				type: "FLASHCARD_DECK",
				taskType: "ETHICAL_SITUATION",
				skill: "READING",
				title: situation.front.title || item.title || "",
				topics: ["Outcomes"],
			},
		};
	});
};

export type ProethicaModuleSummary = { title: string; text: string };

export const extractModuleSummary = (
	rawItems: RawItem[],
): ProethicaModuleSummary | undefined => {
	const item = rawItems.find((rawItem) => rawItem.type === "MODULE_ENDING");
	if (!item) {
		return undefined;
	}
	const c = item.content as { text?: string };
	return {
		title: item.title ?? "Module Summary",
		text: c.text ?? "",
	};
};

const buildAiSimulation = (item: RawItem, id: number): SessionItem => {
	const simulation = (
		item.content as { simulation?: BaseItemContent["simulation"] }
	)?.simulation;

	return {
		type: "ITEM",
		id,
		content: {
			stem: "",
			instruction: "",
			...(simulation ? { simulation } : {}),
		},
		metadata: {
			type: "SCENARIO_INTRO",
			taskType: item.type,
			skill: "READING",
			title: item.title ?? "",
			topics: [item.type === "AI_SIMULATION" ? "Simulation" : "Reflect"],
			scenarioId: item.scenarioId ?? "scenario-1",
		},
	};
};

const buildMcq = (item: RawItem, id: number): SessionItem => {
	const c = item.content as { stem?: string; options?: RawOption[] };
	const options = c.options ?? [];
	const feedback = (item as { feedback?: string }).feedback;

	return {
		type: "ITEM",
		id,
		content: {
			stem: withTitle(item.title, c.stem ?? ""),
			instruction: item.instruction ?? "",
			choices: options.map((opt) => ({
				id: opt.id,
				text: opt.text,
				stem: opt.text,
				isCorrect: opt.isCorrect,
				explanation: feedback,
			})),
			feedback,
		},
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: item.title ?? "",
			topics: ["Knowledge Check"],
		},
		response: {
			maximumScore: 1,
			correctResponse: options
				.filter((o) => o.isCorrect)
				.map((o) => ({ id: o.id })),
			numChoicesRequired: { min: 1, max: 1 },
		},
	};
};

const buildItem = (
	item: RawItem,
	index: number,
	rawItems: RawItem[],
	opts: BuildSessionItemsOptions,
): SessionItem | SessionItem[] => {
	const backendId = Number(item.id);
	const id = Number.isFinite(backendId) ? backendId : baseId + index;

	switch (item.type) {
		case "MODULE_LANDING":
			return buildModuleLanding(item, id, opts);
		case "SCENARIO":
			return buildScenario(item, id);
		case "LESSON":
			return buildLesson(item, id);
		case "PERSPECTIVE":
			return buildPerspective(item, id);
		case "REFLECT":
			return buildReflect(item, id);
		case "EXPLORE": {
			const reviewItem = rawItems.find(
				(r) =>
					r.type === "REVIEW_FLASHCARD" && r.position === item.position + 1,
			);
			return buildExplore(item, id, reviewItem);
		}
		case "REVIEW_FLASHCARD":
			return buildReviewFlashcard(item, id);
		case "ANALYSIS_FLASHCARD":
			return buildAnalysisFlashcard(item, id);
		case "ETHICAL_SITUATION":
			return buildEthicalSituation(item, id);
		case "MODULE_ENDING":
			return [];
		case "AI_SIMULATION":
		case "AI_SIMULATION_REPORT":
			return buildAiSimulation(item, id);
		case "MCQ":
			return buildMcq(item, id);
		default:
			return buildScenario(item, id);
	}
};

export const withEmbeddedAnswer = <T extends object>(
	target: T,
	source: { answer?: SessionItemAnswer | null; lastUpdated?: boolean },
): T =>
	source.answer == null && source.lastUpdated == null
		? target
		: {
				...target,
				...(source.answer != null && { answer: source.answer }),
				...(source.lastUpdated != null && { lastUpdated: source.lastUpdated }),
			};

export const buildSessionItems = (
	rawItems: RawItem[],
	opts: BuildSessionItemsOptions,
) =>
	rawItems.flatMap((item, index) => {
		const built = buildItem(item, index, rawItems, opts);
		return Array.isArray(built)
			? built.map((sessionItem) => withEmbeddedAnswer(sessionItem, item))
			: withEmbeddedAnswer(built, item);
	});
